import { useId, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MoveRight } from 'lucide-react';

import { FormInput } from '@/shared/ui';
import { ActionButton } from '@/shared/ui/action-button';

import type { LocalPallet } from '../model/types';

interface PalletActionBarProps {
  selectedCount: number;
  hasSelectedInPallets: boolean;
  pallets: LocalPallet[];
  /** A save is running: nothing can be moved until it ends */
  disabled?: boolean;
  onMoveToPallet: (palletId: string) => Promise<void>;
  onUnassign: () => Promise<void>;
}

/**
 * What to do with the selected lines. One row on a wide screen; on a phone,
 * "Remove from pallet" on top, then the pallet search labelled "Move N lines to",
 * then the pallets, wrapping.
 */
export function PalletActionBar({
  selectedCount,
  hasSelectedInPallets,
  pallets,
  disabled = false,
  onMoveToPallet,
  onUnassign
}: PalletActionBarProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const searchId = useId();

  // Filter pallets based on search
  const filteredPallets = useMemo(() => {
    if (!search) return pallets;
    const searchLower = search.toLowerCase();
    return pallets.filter((p) => p.palletNumber.toLowerCase().includes(searchLower));
  }, [pallets, search]);

  if (selectedCount === 0) return null;

  return (
    <div className="flex flex-wrap items-end gap-3 p-3 bg-muted/50 rounded-lg border">
      {hasSelectedInPallets && (
        <div className="max-md:basis-full">
          <ActionButton
            type="button"
            size="sm"
            intent="danger"
            className="gap-1 whitespace-nowrap h-8"
            disabled={disabled}
            onClick={onUnassign}>
            {t('pallet.removeFromPallet')}
          </ActionButton>
        </div>
      )}

      {pallets.length > 0 && (
        <>
          <div className="min-w-0 flex-1 md:w-[200px] md:flex-none">
            <FormInput
              id={searchId}
              label={t('pallet.moveToPallet', { count: selectedCount })}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('pallet.searchPallet')}
              className="h-8"
            />
          </div>
          <div className="flex basis-full flex-wrap items-center gap-1 md:basis-auto md:flex-nowrap md:overflow-x-auto">
            {filteredPallets.map((pallet) => (
              <ActionButton
                key={pallet.id}
                type="button"
                size="sm"
                intent="action"
                className="gap-1 whitespace-nowrap h-8"
                disabled={disabled}
                onClick={() => onMoveToPallet(pallet.id)}>
                <MoveRight className="h-3 w-3" />
                {pallet.palletNumber}
              </ActionButton>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
